import {useEffect, useMemo} from 'react';

import {selectEntityData} from 'modules/data';
import {loadReferenceData} from 'modules/data/data-actions';
import {selectIsEntityDataLoading} from 'modules/data/data-selectors';
import {TEntity, IReferenceEntityData} from 'modules/data/data-types';
import {EEntityType} from 'shared/constants/entities';
import { ERequestType } from 'shared/types/requests';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';

import {ICustomSelectEntry} from '../../custom-select';
import {createFieldFormatterForSelect} from '../../custom-select-utils';

export interface ICustomSelectFetchResult {
    data: TEntity[] | null;
    error: any;
}

interface IUseCustomSelectReferenceOptionsFetcherArgs<L, V> {
    referenceUrl?: string;
    referenceUrlQueryParams?: { [index: string]: string };
    labelPath?: string;
    isReferenceNonStorable?: boolean;
    additionalEntries?: ICustomSelectEntry<L, V>[];
    rawDataInStoreFormatter?: (data: any) => any;
    fetchTrigger?: number | string | boolean;
    requestType?: ERequestType;
    setShouldRefetchData?: (value: boolean) => void;
}

export const useCustomSelectReferenceOptionsFetcher = <L, V>({
    referenceUrl,
    labelPath,
    isReferenceNonStorable,
    additionalEntries,
    rawDataInStoreFormatter,
    fetchTrigger,
    requestType = ERequestType.GET,
    setShouldRefetchData,
}: IUseCustomSelectReferenceOptionsFetcherArgs<L, V>) => {
    const dispatch = useAppDispatch();

    const formatFieldLabelForSelect = createFieldFormatterForSelect(labelPath);

    const isFetchingEntries = useAppSelector(state => (!referenceUrl
        ? false : selectIsEntityDataLoading(referenceUrl)(state)));

    const dataInStore = useAppSelector(state => (!referenceUrl ? null : (
        selectEntityData(referenceUrl, EEntityType.REFERENCE)(state) as IReferenceEntityData
    )?.rows));

    const dataInStoreMutated: TEntity[] | null | undefined = useMemo(() => {
        if (!rawDataInStoreFormatter) return dataInStore;
        return rawDataInStoreFormatter(dataInStore);
    }, [rawDataInStoreFormatter, dataInStore]);

    const entriesInStore = useMemo(() => dataInStoreMutated?.map(dataEntry => ({
        label: formatFieldLabelForSelect(dataEntry),
        value: dataEntry as any,
    })), [dataInStoreMutated]);

    useEffect(() => {
        (async () => {
            if (!isFetchingEntries && !isReferenceNonStorable && !!referenceUrl
                && (!dataInStoreMutated || fetchTrigger)) {
                await dispatch(loadReferenceData(
                    referenceUrl,
                    {requestType},
                ));

                if (setShouldRefetchData && fetchTrigger) {
                    setShouldRefetchData(false);
                }
            }
        })();
    }, [referenceUrl, fetchTrigger]);

    return {
        entriesFromFetcher: additionalEntries && entriesInStore
            ? additionalEntries.concat(entriesInStore)
            : entriesInStore,
        isFetchingEntries,
    };
};

export type TUseCustomSelectReferenceOptionsFetcher = ReturnType<typeof useCustomSelectReferenceOptionsFetcher>;
